import * as React from 'react';
import * as _ from 'lodash';
import { SupportContext } from '../shared/support-context';
import SupportTicket from '../models/supportTicket';
import FilterType from '../models/enums/filterType';
import moment from 'moment';
import CommentData from '../models/commentData';
import SupportRequest from '../models/supportRequest';
import useRestService from '../shared/rest-service';

interface SortOptions {
  orderDirection?: 'asc' | 'desc';
  orderColumn?: string;
  searchTerm?: string;
  count?: number;
  createdDateStart?: moment.Moment | string;
  createdDateEnd?: moment.Moment | string;
  startAt?: number;
}

/**
 * useSupport
 * @function useSupport - Custom hook for the support page
 */
const useSupport = () => {
  const {
    loading,
    tickets,
    dispatch,
    orderDirection,
    orderColumn,
    allFilters,
    localSelectedFilters,
    searchQuery,
    pageSize
  } = React.useContext(SupportContext);

  const { getAuth, postAuth } = useRestService();

  /**
   * getSupportTickets
   * @method getSupportTickets - API call to receive the support tickets
   * @param clientId Identifier of the client to which the tickets belong
   * @param filters Optional filters to retrieve specific tickets
   */
  const getSupportTickets = (clientId: number, filters: any) => {
    return postAuth(`api/clients/${clientId}/support/tickets/list`, filters)
      .then((json: any) => {
        return json;
      })
      .catch(err => err);
  };

  /**
   * getSupportTicket
   * @method getSupportTicket - API call to retrieve a single support ticket
   * @param clientId Identifier of the client to which the ticket belongs to
   * @param issueId Identifier of the support ticket
   */
  const getSupportTicket = (clientId: number, issueId: string) => {
    return postAuth(`api/clients/${clientId}/support/tickets/${issueId}`, null)
      .then((json: any) => {
        return json;
      })
      .catch(err => err);
  };

  /**
   * getSupportTicket
   * @method getSupportTicket - API call to retrieve a single support ticket
   * @param clientId Identifier of the client to which the ticket belongs to
   * @param issueId Identifier of the support ticket
   */
  const postTicket = (clientId: number, options: any) => {
    return postAuth(`api/clients/${clientId}/support/tickets`, options)
      .then((json: any) => {
        return json;
      })
      .catch(err => err);
  };

  /**
   * postComments
   * @method postComments - API call to add a comment to a support ticket
   * @param clientId Identifier of the client to which the ticket belong
   * @param issueId Identifier of the support ticket
   * @param comment Information about the comment to be added
   */
  const postComments = (clientId: number, issueId: string, comment: CommentData) => {
    return postAuth(`api/clients/${clientId}/support/tickets/${issueId}/comments`, comment)
      .then((json: any) => {
        return json;
      })
      .catch(err => err);
  };

  /**
   * getSupportFilters
   * @method getSupportFilters - A group of API calls to receive the different filters available for support
   * @param clientId Identifier of the client to which the tickets belong
   * @returns An object that contains filters { organizations, types, statuses, requesters }
   */
  const getSupportFilters = (clientId: number) => {
    /** @const getOrganizations - Gets the organization filters */
    const getOrganizations = getAuth(`api/clients/${clientId}/support/organizations`)
      .then(json => {
        return json.length > 0 ? json.map(item => item.name) : json;
      })
      .catch(err => err);

    /** @const getTypes - Gets the types filters */
    const getTypes = getAuth(`api/clients/${clientId}/support/types`)
      .then(json => {
        return json;
      })
      .catch(err => err);

    /** @const getStatuses - Gets the statuses filters */
    const getStatuses = getAuth(`api/clients/${clientId}/support/statuses`)
      .then(json => {
        return json;
      })
      .catch(err => err);

    /** @const getRequesters - Gets the requesters filters */
    const getRequesters = getAuth(`api/clients/${clientId}/support/requesters`)
      .then(json => {
        return json;
      })
      .catch(err => err);

    return Promise.all([getOrganizations, getTypes, getStatuses, getRequesters]).then(
      ([organizations, types, statuses, requesters]) => {
        return {
          organizations,
          types,
          statuses,
          requesters
        };
      }
    );
  };

  /**
   * fetchSupportTickets
   * @function fetchSupportTickets - Fetches the support tickets from the backend and sets them to the state
   * @param clientId Id of the active client
   * @param options Options to narrow down search results
   * @param firstRender Optional boolean argument indicating whether this call is the first render
   */
  const fetchSupportTickets = (clientId: number, options?: SortOptions, firstRender?: boolean) => {
    dispatch({ loading: true });

    if (options) {
      options.createdDateStart = options.createdDateStart ? moment(options.createdDateStart).format('YYYY/MM/DD') : '';
      options.createdDateEnd = options.createdDateEnd ? moment(options.createdDateEnd).format('YYYY/MM/DD') : '';
    }

    return getSupportTickets(clientId, options || null)
      .then((response: any) => {
        response.tickets.map((ticket: SupportTicket) => {
          ticket.createdTime = new Date(ticket.createdTime)
            .toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: '2-digit',
              hour: '2-digit',
              minute: '2-digit'
            })
            .replace(',', '');

          ticket.updatedTime = new Date(ticket.updatedTime)
            .toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: '2-digit',
              hour: '2-digit',
              minute: '2-digit'
            })
            .replace(',', '');
          return ticket;
        });

        !firstRender &&
          dispatch({
            totalTickets: response.totalTickets,
            tickets: response.tickets,
            orderDirection: options && options.orderDirection,
            orderColumn: options && options.orderColumn,
            searchQuery: options && options.searchTerm,
            pageSize: options && options.count,
            currentPage: options && options.startAt,
            loading: false
          });

        return {
          totalTickets: response.totalTickets,
          tickets: response.tickets
        };
      })
      .catch(() => dispatch({ loading: false }));
  };

  /**
   * fetchSupportInformation
   * @function fetchSupportInformation - Fetches support ticket information and sets it into context
   * @param clientId Identifier of the client to which the tickets belong
   * @param options Options to narrow down search results
   */
  const fetchSupportInformation = (clientId: number, options: SortOptions) => {
    Promise.all([fetchSupportTickets(clientId, options, true), getSupportFilters(clientId)]).then(
      ([supportInfo, allFilters]) => {
        dispatch({ ...supportInfo, allFilters, loading: false });
      }
    );
  };

  /**
   * addComment
   * @function addComment - Adds a comment to an issue
   * @param clientId Identifier of the client to which the tickets belong
   * @param issueId Identifier of the support issue
   * @param comment Comment details
   */
  const addComment = (clientId: number, issueId: string, comment: CommentData) => {
    postComments(clientId, issueId, comment).then(() => {
      getSupportTicket(clientId, issueId).then(ticket => {
        const ticketsClone = _.cloneDeep(tickets);
        const updatedTickets = ticketsClone.map(issue => {
          if (ticket.issueName === issue.issueName) {
            return ticket;
          }
          return issue;
        });
        dispatch({ tickets: updatedTickets });
      });
    });
  };

  const addSupportTicket = (clientId: number, request: SupportRequest) => {
    return postTicket(clientId, request)
      .then(json => {
        return json;
      })
      .catch(err => err);
  };

  /**
   * clearAllFilters
   * @function clearAllFilters Unchecks all the filters
   */
  const clearAllFilters = (clientId: number) => {
    const localFilters = {
      requestTypeSelected: [],
      statusSelected: [],
      requesterSelected: [],
      organizationSelected: [],
      createdDateStart: null,
      createdDateEnd: null
    };

    dispatch({
      selectedFilters: localFilters,
      localSelectedFilters: localFilters
    });

    fetchSupportTickets(clientId, {
      ...localFilters,
      orderColumn,
      orderDirection,
      searchTerm: searchQuery,
      startAt: 0,
      count: pageSize
    });
  };

  /**
   * countFilters
   * @function countFilters - Counts the amount of filters that have been applied. Excludes null values and incomplete dates
   */
  const countFilters = () => {
    return [].concat.apply([], Object.values(localSelectedFilters)).filter(item => {
      if (!item || (item._isAMomentObject && !item._isValid)) {
        return;
      }

      return item;
    }).length;
  };

  /**
   * updateLocalFilters
   * @function updateLocalFilters - Updates the local filters as the user is updating
   * @param filterType Type of filter to be applied
   * @param value Name or date of the filter
   * @param checked Boolean value indicating whether the checkbox is checked
   * @param startDate Boolean value indicating whether the date is a start date
   */
  const updateLocalFilters = (filterType: string, value: string, checked?: boolean, startDate?: boolean) => {
    let updatedFilters;
    if (filterType === FilterType.CreatedDate) {
      if (startDate) {
        return dispatch({
          localSelectedFilters: {
            ...localSelectedFilters,
            createdDateStart: value
          }
        });
      }
      return dispatch({
        localSelectedFilters: {
          ...localSelectedFilters,
          createdDateEnd: value
        }
      });
    }
    if (checked) {
      if (!value) {
        switch (filterType) {
          case FilterType.Organization:
            updatedFilters = [];
            dispatch({
              localSelectedFilters: {
                ...localSelectedFilters,
                organizationSelected: [...updatedFilters]
              }
            });
            break;
          case FilterType.Requester:
            updatedFilters = [];
            dispatch({
              localSelectedFilters: {
                ...localSelectedFilters,
                requesterSelected: [...updatedFilters]
              }
            });
            break;
          case FilterType.Status:
            updatedFilters = [];
            dispatch({
              localSelectedFilters: {
                ...localSelectedFilters,
                statusSelected: [...updatedFilters]
              }
            });
            break;
          case FilterType.Type:
            updatedFilters = [];
            dispatch({
              localSelectedFilters: {
                ...localSelectedFilters,
                requestTypeSelected: [...updatedFilters]
              }
            });
            break;
        }
        return;
      }
      switch (filterType) {
        case FilterType.Organization:
          updatedFilters = localSelectedFilters.organizationSelected.filter(item => item !== value);
          dispatch({
            localSelectedFilters: {
              ...localSelectedFilters,
              organizationSelected: updatedFilters
            }
          });
          break;
        case FilterType.Requester:
          updatedFilters = localSelectedFilters.requesterSelected.filter(item => item !== value);
          dispatch({
            localSelectedFilters: {
              ...localSelectedFilters,
              requesterSelected: updatedFilters
            }
          });
          break;
        case FilterType.Status:
          updatedFilters = localSelectedFilters.statusSelected.filter(item => item !== value);
          dispatch({
            localSelectedFilters: {
              ...localSelectedFilters,
              statusSelected: updatedFilters
            }
          });
          break;
        case FilterType.Type:
          updatedFilters = localSelectedFilters.requestTypeSelected.filter(
            item => item !== value && item.text !== value
          );
          dispatch({
            localSelectedFilters: {
              ...localSelectedFilters,
              requestTypeSelected: updatedFilters
            }
          });
          break;
      }
    } else {
      if (!value) {
        switch (filterType) {
          case FilterType.Organization:
            updatedFilters = allFilters.organizations;
            dispatch({
              localSelectedFilters: {
                ...localSelectedFilters,
                organizationSelected: updatedFilters
              }
            });
            break;
          case FilterType.Requester:
            updatedFilters = allFilters.requesters;
            dispatch({
              localSelectedFilters: {
                ...localSelectedFilters,
                requesterSelected: updatedFilters
              }
            });
            break;
          case FilterType.Status:
            updatedFilters = allFilters.statuses;
            dispatch({
              localSelectedFilters: {
                ...localSelectedFilters,
                statusSelected: updatedFilters
              }
            });
            break;
          case FilterType.Type:
            updatedFilters = allFilters.types;
            dispatch({
              localSelectedFilters: {
                ...localSelectedFilters,
                requestTypeSelected: [...updatedFilters]
              }
            });
            break;
        }
        return;
      }
      switch (filterType) {
        case FilterType.Organization:
          updatedFilters = localSelectedFilters.organizationSelected.concat([value]);
          dispatch({
            localSelectedFilters: {
              ...localSelectedFilters,
              organizationSelected: updatedFilters
            }
          });
          break;
        case FilterType.Requester:
          updatedFilters = localSelectedFilters.requesterSelected.concat([value]);
          dispatch({
            localSelectedFilters: {
              ...localSelectedFilters,
              requesterSelected: updatedFilters
            }
          });
          break;
        case FilterType.Status:
          updatedFilters = localSelectedFilters.statusSelected.concat([value]);
          dispatch({
            localSelectedFilters: {
              ...localSelectedFilters,
              statusSelected: updatedFilters
            }
          });
          break;
        case FilterType.Type:
          updatedFilters = localSelectedFilters.requestTypeSelected.concat([value]);
          dispatch({
            localSelectedFilters: {
              ...localSelectedFilters,
              requestTypeSelected: updatedFilters
            }
          });
          break;
      }
    }
  };

  return {
    loading,
    tickets,
    fetchSupportTickets,
    fetchSupportInformation,
    orderDirection,
    orderColumn,
    allFilters,
    updateLocalFilters,
    clearAllFilters,
    countFilters,
    addComment,
    addSupportTicket
  };
};

export default useSupport;
